import { Preferences } from '@capacitor/preferences';
import { IncidentReportingApi } from '@deckee/api-app-main';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AppAsyncThunkAPI } from '../..';
import apiConfiguration from '../../../backend/apiConfiguration';
import captureSpecificError from '../../../sentry';
import { AvailableTypesList, SelectedArea, VideoUpload } from './models';

export const api = new IncidentReportingApi(apiConfiguration);

export const setLocation = createAction(
  '[Incident report] set location',
  (latitude, longitude) => ({
    payload: {
      latitude,
      longitude,
    },
  }),
);

export const setArea = createAction(
  '[Incident report] set Area',
  (selectedArea: SelectedArea) => ({
    payload: {
      selectedArea,
    },
  }),
);

export const setType = createAction(
  '[Incident report] set type',
  (type: AvailableTypesList | null) => ({
    payload: {
      type,
    },
  }),
);

export const resetTags = createAction('[Incident report] reset tags');

export const reset = createAction('[Incident report] reset');

export const addIncidentDetails = createAction(
  '[Incident report] Add incident details',
  ({ tags, incidentDateTime }) => ({
    payload: { tags, incidentDateTime },
  }),
);

export const addIncidentVideo = createAction(
  '[Incident report] Add incident video',
  (upload: VideoUpload) => ({
    payload: upload,
  }),
);

export const updateIncidentVideos = createAction(
  '[Incident report] Update incident videos',
  (videoUploads) => ({
    payload: videoUploads,
  }),
);

export const setOfflineInitiated = createAction(
  '[Incident report] Set offline initiated',
);

export const addIncidentDescription = createAction(
  '[Incident report] Add incident description',
  ({ vesselRegistration, description, imageUrls, videoUploads }) => ({
    payload: { vesselRegistration, description, imageUrls, videoUploads },
  }),
);

export const checkOfflineIncidentReport = createAsyncThunk<
  void,
  void,
  AppAsyncThunkAPI
>(`[Incident report] Check offline incident report`, async (_) => {
  const OFFLINE_INCIDENT_REPORT_KEY = 'incident-report';

  // get the preferences stored in capacitor/preferences
  const { value } = await Preferences.get({ key: OFFLINE_INCIDENT_REPORT_KEY });

  try {
    const offlineIncidentReport = JSON.parse(value || '{}');

    // if there is no offline incident report, return
    if (!offlineIncidentReport) {
      return;
    }

    // if there is no details about an offline incident, return
    const { description, latitude, longitude, incidentDate } =
      offlineIncidentReport;
    if (!description) {
      return;
    }

    // Make a request to send a notification to the user about thier offline incident report
    await api.createOfflineIncidentNotification({
      createOfflineIncidentNotificationRequest: {
        description,
        latitude,
        longitude,
        incidentDate,
      },
    });
  } catch (e: unknown) {
    // Something has gone wrong. Report it to Sentry so we can track it.
    captureSpecificError(
      e,
      'containers/app-main-client/src/services/store/modules/incident-report/actions.ts',
      {
        deckeeId:
          '[incident-report actions] Error parsing offline incident reporting',
      },
    );
  } finally {
    // Regardless of whether the request was successful or not, remove the offline incident report.
    // This is mostly to avoid the situation where the user will be spammed with notifications about
    // their offline report
    await Preferences.remove({ key: OFFLINE_INCIDENT_REPORT_KEY });
  }
});
